import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, BookOpen, ShieldCheck, AlertCircle, X, MessageSquare, ChevronDown, ChevronUp,
} from "lucide-react";
import { useModGetAssignmentsQuery, useModGetResultsQuery, useModSubmitFeedbackMutation } from "../../api/moderatorApi";

function extractError(err: any): string {
  return err?.data?.message ?? err?.data?.error ?? err?.error ?? "An error occurred";
}

const STATUS_MAP: Record<string, string> = {
  pending:           "bg-gray-50 text-gray-400 border-gray-100",
  under_moderation:  "bg-purple-50 text-purple-700 border-purple-100",
  moderated:         "bg-violet-50 text-violet-700 border-violet-100",
  moderation_rework: "bg-orange-50 text-orange-700 border-orange-100",
  examiner_rework:   "bg-rose-50 text-rose-700 border-rose-100",
  published:         "bg-emerald-50 text-emerald-700 border-emerald-100",
};

const GRADE_COLOR: Record<string, string> = {
  A: "text-emerald-600", B: "text-emerald-500", C: "text-blue-500",
  D: "text-amber-500", E: "text-orange-500", F: "text-rose-500",
};

export default function ModeratorCoursesPage() {
  const { data: assignments = [], isLoading } = useModGetAssignmentsQuery();
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-5">
          <Link to="/exams/moderator" className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm">
            <ArrowLeft className="w-4 h-4 text-gray-500" />
          </Link>
          <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
            <BookOpen className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Assigned Courses</h1>
            <p className="text-gray-400 text-sm mt-1.5 font-medium">{assignments.length} course{assignments.length !== 1 ? "s" : ""} to moderate.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
            Courses {assignments.length > 0 ? `(${assignments.length})` : ""}
          </div>
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-200" /></div>
        ) : assignments.length === 0 ? (
          <div className="py-24 text-center">
            <BookOpen className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No courses assigned yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {(assignments as any[]).map((a: any) => {
              const isOpen = expanded === String(a.id);
              const status = (a.status ?? "pending").toLowerCase();

              return (
                <div key={a.id}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : String(a.id))}
                    className="w-full flex items-center justify-between px-8 py-5 hover:bg-gray-50/80 transition-all text-left">
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-gray-900 uppercase tracking-tight">
                        {a.course_name ?? `Course ${a.course_id}`}
                        {a.course_code && <span className="ml-2 text-[10px] text-gray-400 font-mono normal-case">{a.course_code}</span>}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                        Diet: {a.diet_name ?? a.diet_id}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border shadow-sm ${STATUS_MAP[status] ?? "bg-gray-50 text-gray-400 border-gray-100"}`}>
                        {status.replace(/_/g, " ")}
                      </span>
                      {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </button>

                  {isOpen && a.batch_id && (
                    <CourseDetail
                      courseId={String(a.course_id)}
                      dietId={String(a.diet_id)}
                      batchId={String(a.batch_id)}
                      existingComment={a.moderator_comment}
                    />
                  )}
                  {isOpen && !a.batch_id && (
                    <div className="px-8 pb-6 text-xs text-gray-400 font-medium">
                      Results have not been submitted by the examiner yet.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-violet-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-violet-500/20">
          <ShieldCheck className="w-8 h-8 text-violet-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-violet-400 mb-2">Moderation Guidelines</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Review the scores and submit your feedback. Your feedback will be forwarded to the Chief Moderator
            who will consolidate it and pass it to the Chief Examiner for corrective action where necessary.
          </p>
        </div>
      </div>
    </div>
  );
}

function CourseDetail({ courseId, dietId, batchId, existingComment }: {
  courseId: string; dietId: string; batchId: string; existingComment?: string;
}) {
  const { data, isLoading } = useModGetResultsQuery({ courseId, dietId });
  const [submitFeedback, { isLoading: submitting }] = useModSubmitFeedbackMutation();
  const [comment, setComment] = useState(existingComment ?? "");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const scores  = (data as any)?.data ?? [];
  const status  = (data as any)?.status;
  const alreadySubmitted = status === "moderated" || status === "moderation_rework" || status === "examiner_rework" || status === "published";

  const handleSubmit = async () => {
    if (!comment.trim()) { setError("Please enter your feedback."); return; }
    setError("");
    try {
      await submitFeedback({ id: batchId, comment }).unwrap();
      setSuccess(true);
    } catch (err) { setError(extractError(err)); }
  };

  return (
    <div className="px-8 pb-6 space-y-4">
      {isLoading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-200" /></div>
      ) : (
        <>
          {/* Scores table */}
          {scores.length > 0 ? (
            <div className="border border-gray-100 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 bg-gray-50 text-[9px] font-black uppercase tracking-widest text-gray-400">
                Scores ({scores.length} students)
              </div>
              <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
                {scores.map((s: any, i: number) => (
                  <div key={i} className="flex items-center justify-between px-5 py-2.5 text-sm">
                    <div>
                      <div className="font-bold text-gray-800">{s.student_name ?? s.student_reg}</div>
                      <div className="text-[10px] text-gray-400 font-mono">{s.student_reg}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-gray-700">{s.score}</span>
                      <span className={`font-black text-base w-6 text-center ${GRADE_COLOR[s.grade] ?? "text-gray-500"}`}>{s.grade}</span>
                      {s.version > 1 && <span className="text-[9px] bg-amber-50 text-amber-600 border border-amber-100 px-2 py-0.5 rounded-full font-black">v{s.version}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400">No scores available yet.</p>
          )}

          {/* Feedback form */}
          {success ? (
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-5 py-4 text-sm text-emerald-700 font-bold">
              Feedback submitted successfully.
            </div>
          ) : alreadySubmitted && !existingComment ? (
            <div className="bg-violet-50 border border-violet-100 rounded-2xl px-5 py-4 text-sm text-violet-700 font-bold">
              Feedback already submitted for this batch.
            </div>
          ) : (
            <div className="space-y-3">
              {existingComment && (
                <div className="bg-violet-50 border border-violet-100 rounded-xl px-4 py-3 text-sm text-violet-800 font-medium">
                  <span className="font-black text-[9px] uppercase tracking-widest text-violet-500 block mb-1">Previously Submitted Feedback</span>
                  {existingComment}
                </div>
              )}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3" /> Your Feedback
                </label>
                <textarea rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={alreadySubmitted}
                  placeholder="Enter your moderation observations and recommendations…"
                  className="w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 text-sm outline-none focus:border-violet-500 bg-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              {error && (
                <p className="text-[10px] font-black uppercase tracking-widest text-rose-500 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> {error}
                </p>
              )}
              {!alreadySubmitted && (
                <button onClick={handleSubmit} disabled={submitting}
                  className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-violet-600 transition-all disabled:opacity-30 active:scale-95">
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                  {submitting ? "Submitting…" : "Submit Feedback"}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
